import { Type } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { TextListItem } from "app/shared/model/app.model";
import { Utils } from "ng/src/app/core/utils";
import { KeyOf, Nullable, Nullish, PartialExcept } from "ng/src/app/shared/model/app.model";
import { TemplateSegment, TemplateSegmentType, TemplateSegmentTypeId, TemplateTypeId } from "ng/src/app/templates/shared/template.model";

export type TemplateSegmentPropertyKey = KeyOf<TemplateSegment, 'name' | 'required' | 'key' | 'calculate' | 'variable'>;
type TemplateSegmentTypeProperties = TemplateSegmentType['properties'];

// Checks if the template segment type supports the given property based on the template type
export function doesTemplateSegmentPropertiesSupportProperty({key, properties, templateType}: {
	key: TemplateSegmentPropertyKey,
	properties: TemplateSegmentTypeProperties,
	templateType: TemplateTypeId
}): boolean {
	switch (key) {
		case 'name':
			return properties.NAME === true;
		case 'required':
			return properties.SIMPLE_REQUIRED_INPUT === true;
		case 'key':
			return properties.KEY === true;
		case 'calculate':
			return properties.CALCULATE === true;
		case 'variable':
			return templateType !== TemplateTypeId.INSPECTION && (properties.REQUIRED_RANGE_INPUT === true || properties.SIMPLE_REQUIRED_INPUT === true || properties.CALCULATE === true);
	}
}

export function createSegmentEditorFormGroup<TType extends TemplateTypeId, SType extends TemplateSegmentTypeId>(templateType: TType, segment: PartialExcept<TemplateSegment, 'type'>): SegmentEditorForm {
	const segmentType: TemplateSegmentType = segment.type;
	
	const valueMap: Record<TemplateSegmentTypeId, ValueControlOption[]> = {
		[TemplateSegmentTypeId.LITERAL_TEXT]: ['text'],
		[TemplateSegmentTypeId.TAG_CHOICE]: ['tagValue', 'depth', 'minRequired', 'maxAllowed'],
		[TemplateSegmentTypeId.TAG]: ['tagValue'],
		[TemplateSegmentTypeId.TEXT_INPUT]: [defaultBuilder('textOption', 'PLAIN_TEXT_ONLY'), defaultBuilder('width', 20), 'height', 'textArea', requiredBooleanBuilder('barcode')],
		[TemplateSegmentTypeId.SUB_TEMPLATE]: ['level', requiredBooleanBuilder('print'), requiredBooleanBuilder('requireCommentOnAbnormal')],
		[TemplateSegmentTypeId.CHECKBOX]: [],
		[TemplateSegmentTypeId.FILE_ATTACHMENT]: ['minRequired', 'maxAllowed'],
		[TemplateSegmentTypeId.TEXT_CHOICE]: ['choiceList', 'defaultChoice'],
		[TemplateSegmentTypeId.NUMERIC_INPUT]: ['units', 'minValue', 'maxValue', 'belowMinAlertMessage', 'aboveMaxAlertMessage'],
		[TemplateSegmentTypeId.DATE_TIME]: [requiredBooleanBuilder('includeDate'), requiredBooleanBuilder('includeTime')],
		[TemplateSegmentTypeId.USER_CHOOSER]: ['users'],
		[TemplateSegmentTypeId.SIGNOFF]: [requiredBooleanBuilder('requireAllSignoffs'), 'text', 'altText',
			requiredBooleanBuilder('sendPendingSignoffNotification'), requiredBooleanBuilder('requireOnShift'), requiredBooleanBuilder('templateUsersCanEdit'),
			defaultBuilder('userGroups', []), defaultBuilder('users', []), defaultBuilder('editUsers', []), 'level', defaultBuilder('allowAnyUser', true),
			requiredBooleanBuilder('allowReject'), requiredBooleanBuilder('allowUndo')],
		[TemplateSegmentTypeId.SIGNATURE]: ['textList', 'text']
	};
	
	switch (templateType) {
		case TemplateTypeId.INSPECTION:
			valueMap[TemplateSegmentTypeId.TEXT_INPUT].push('textAlignment');
	}
	
	const doPropertiesSupport: (key: TemplateSegmentPropertyKey) => boolean = (key) => {
		return doesTemplateSegmentPropertiesSupportProperty({key: key, properties: segmentType.properties, templateType: templateType});
	};
	
	const propertyMap: Record<TemplateSegmentPropertyKey, FormControl | null> = {
		name: doPropertiesSupport('name') ? createFormControl('name', segment.name ?? '') : null,
		required: doPropertiesSupport('required') ? createFormControl('required', segment.required ?? false) : null,
		key: doPropertiesSupport('key') ? createFormControl('key', segment.key ?? false) : null,
		calculate: doPropertiesSupport('calculate') ? createFormControl('calculate', segment.calculate ?? false) : null,
		variable: doPropertiesSupport('variable') ? createFormControl('variable', segment.variable ?? false) : null,
	}
	
	const valueControls = Utils.arrayToMap(valueMap[segmentType.id], value => {
		let key: keyof TemplateSegment;
		let defaultVal = Nullable.of<TemplateSegment[typeof key]>();
		
		if (typeof value === 'string') {
			key = value;
		}
		else {
			key = value.key;
			defaultVal = value.defaultValue;
		}
		
		const control = createFormControl(key, segment[key] ?? defaultVal);
		
		return [key, control];
	});
	
	const propertyControls = Utils.arrayToMap(Object.entries(propertyMap)
		.filter(entry => entry[1]), entry => [
			entry[0] as keyof typeof propertyMap,
		entry[1] as FormControl<Nullable<TemplateSegment[keyof typeof propertyMap]>>
	]);
	
	return new FormGroup({
		...valueControls,
		...propertyControls
	}, {
		validators: createFormValidators()
	}) as unknown as SegmentEditorForm;
	
	
	
	function createFormControl(key: 'textList', currentVal: Nullable<TemplateSegment['textList']>): FormArray
	function createFormControl<K extends keyof TemplateSegment>(key: K, currentVal: Nullable<TemplateSegment[K]>): FormControl<Nullable<TemplateSegment[K]>>
	function createFormControl<K extends keyof TemplateSegment>(key: K, currentVal: Nullable<TemplateSegment[K]> = null): FormArray | FormControl<Nullable<TemplateSegment[K]>> {
		switch (key) {
			case 'textList':
				const array = new FormArray(segment.textList?.map(e => createSignatureListItem(e)) ?? []);
				return createIterableFormArray(array);
			default:
				return new FormControl<Nullable<TemplateSegment[K]> | null>(currentVal, { validators: createValidators(key) })
		}
	}
	
	function createValidators<K extends keyof TemplateSegment>(key: K) {
		const segmentType = segment.type.id;
		const validators = [] as ValidatorFn[];
		
		switch (key) {
			case 'name':
				switch (segmentType) {
					case TemplateSegmentTypeId.SIGNATURE:
						break;
					default:
						validators.push(Validators.required);
				}
				break;
			case 'text':
				if (segmentType !== TemplateSegmentTypeId.SIGNOFF) {
					validators.push(Validators.required);
				}
				break;
			case 'tagValue':
				switch (segmentType) {
					case TemplateSegmentTypeId.TAG:
						validators.push(Validators.required);
						break;
					case TemplateSegmentTypeId.TAG_CHOICE:
						validators.push(Validators.max(1));
						break;
				}
				break;
			case 'choiceList':
				validators.push(Validators.required)
				break;
			case 'level':
				if (templateType === TemplateTypeId.INSPECTION)
					validators.push(
						Validators.min(1),
						Validators.max(5)
					)
				break;
			case 'width':
				validators.push(Validators.required)
				break;
			case 'height':
				validators.push(
					Validators.max(100),
					Validators.min(1)
				)
				break;
				
			case 'minRequired':
			case 'maxAllowed':
				validators.push(Validators.min(1))
				break;
		}
		
		
		return validators;
	}
	
	function createFormValidators(): ValidatorFn[] {
		const validators = [] as ValidatorFn[];
		
		validators.push(
			formValueGetterValidator(FormGroup,
				(form) => betweenValidator(() => form.get(['minRequired'])?.value, () => form.get(['maxAllowed'])?.value)
			),
			formValueGetterValidator(FormGroup,
					form => betweenValidator(() => form.get(['minValue'])?.value, () => form.get(['maxValue'])?.value)
			)
		)
		
		return validators;
	}
}

export function addBlankSignatureListItem(formArray: FormArray) {
	formArray.push(createSignatureListItem());
}

function createSignatureListItem(item?: TextListItem): TextListItemControl {
	if (!item) item = { required: false, text: '' };
	
	return new FormGroup({
		required: new FormControl(item.required),
		text: new FormControl(item.text, { validators: [Validators.required] })
	});
}

function formValueGetterValidator<T extends AbstractControl>(type: Type<T>, getter: (c: T) => Nullable<ValidationErrors>): ValidatorFn {
	return function(control: AbstractControl) {
		if (!(control instanceof type)) return null;
		return getter(control);
	}
}

function requiredBooleanBuilder<K extends keyof TemplateSegment>(key: K): { key: K, defaultValue: boolean } {
	return defaultBuilder(key, false as any) as any;
}

function defaultBuilder<K extends keyof TemplateSegment>(key: K, defaultValue: TemplateSegment[K]): { key: K, defaultValue: TemplateSegment[K] } {
	return { key, defaultValue }
}

function betweenValidator(min: () => Nullish<number>, max: () => Nullish<number>) {
	return function(control) {
		const minVal = min();
		const maxVal = max();
		
		const minErrors = minVal != null ? Validators.min(minVal)(control) : null;
		const maxErrors = maxVal != null ? Validators.max(maxVal)(control) : null;
		
		if (minErrors || maxErrors) {
			return {
				...minErrors && minErrors,
				...maxErrors && maxErrors
			}
		}
		return null;
		
	} as TypedValidatorFn<Nullable<number>>
}

function createIterableFormArray<T extends AbstractControl>(array: FormArray<T>): IterableFormArray<T> {
	(array as any)[Symbol.iterator] = function*() {
		for (let i = 0; i < array.length; i++) {
			yield array.at(i);
		}
	}
	return array as IterableFormArray<T>;
}

export interface TypedValidatorFn<T> extends ValidatorFn {
	(control: FormControl<T>): ValidationErrors | null;
}

export interface SegmentEditorForm extends FormGroup<SegmentEditorControlMap> {
	controls: SegmentEditorControlMap
}

export type SegmentEditorControl<K extends keyof TemplateSegment> = K extends 'textList' ? FormArray<TextListItemControl> : FormControl<Nullable<TemplateSegment[K]>>

type SegmentEditorControlMap = {
	[U in keyof TemplateSegment]?: SegmentEditorControl<U>
}
type TextListItemControl = FormGroup<{
	required: FormControl<Nullable<boolean>>,
	text: FormControl<Nullable<string>>
}>
type IterableFormArray<T extends AbstractControl> = FormArray<T> & { [Symbol.iterator]: Iterator<T>}
type ValueControlOption<K extends keyof TemplateSegment = keyof TemplateSegment> = K | { key: K, defaultValue: TemplateSegment[K] }